import type { DigestLink } from '@reditelji/shared';
import { useNavigate } from 'react-router';
import { useUi } from '../state/ui';

/** Navigation into the planning screens shared by Pregled and the AI digest. */
export function useDeepLinks() {
  const navigate = useNavigate();
  const {
    setSelectedDay,
    selectShift,
    selectGap,
    setAsideTab,
    setBoardView,
    setSectorFocus,
    revealTimelineAt,
  } = useUi();

  const goShift = (eventDayId: string, shiftId: string | null) => {
    setSelectedDay(eventDayId);
    if (shiftId) {
      selectShift(shiftId);
      setAsideTab('podrobnosti');
    }
    void navigate('/casovnica');
  };
  // `shiftId` when the gap is an open shift row, e.g. one a "Ne pride" freed.
  // Selecting it opens Podrobnosti with the candidates for it.
  const goGap = (g: {
    eventDayId: string;
    postId: string;
    fromMin: number;
    toMin: number;
    shiftId?: string;
  }) => {
    setSelectedDay(g.eventDayId);
    selectGap({ postId: g.postId, fromMin: g.fromMin, toMin: g.toMin, shiftId: g.shiftId });
    void navigate('/casovnica');
  };
  const goTimeline = (eventDayId: string) => {
    setSelectedDay(eventDayId);
    setAsideTab('opozorila');
    void navigate('/casovnica');
  };
  /**
   * A sector at a moment of a day, from the Pregled heatmap. The order matters:
   * picking the day clears the sector focus, and the people view ignores it.
   */
  const goTimelineAt = (eventDayId: string, sectorName: string, min: number) => {
    setSelectedDay(eventDayId);
    setBoardView('mesta');
    setSectorFocus(sectorName);
    setAsideTab('opozorila');
    revealTimelineAt(eventDayId, min);
    void navigate('/casovnica');
  };
  const goPerson = (id: string, tab?: 'sporocila' | 'organizacija') =>
    void navigate(`/prostovoljci?volunteer=${id}${tab ? `&tab=${tab}` : ''}`);
  /**
   * External teams live as a second view on Prostovoljci (SPEC §1.1J), so a
   * slot's row opens its team rather than a person card it will never have.
   */
  const goTeam = (groupId: string) =>
    void navigate(`/prostovoljci?view=ekipe&ekipa=${groupId}`);

  const openDigestLink = (link: DigestLink | null) => {
    if (!link) return;
    if (link.kind === 'volunteer') goPerson(link.volunteerId, 'sporocila');
    else if (link.kind === 'gap') goGap(link);
    else goTimeline(link.eventDayId);
  };

  return { goShift, goGap, goTimeline, goTimelineAt, goPerson, goTeam, openDigestLink };
}
